import ProvidersModalContext, { ProvidersModalPurpose } from '@app/src/context/ProvidersModalContext'
import React, { useCallback, useMemo, useState } from 'react'
import { useDialogState } from '../hooks/mui-hooks'
import type { Provider, ProviderOrganizationResult } from '../types/organizations'

interface Props {
  children: JSX.Element
}

const ProvidersModalContextProvider = ({ children }: Props): JSX.Element => {
  const [isConnectDialogOpen, openConnectDialog, closeConnectDialog] = useDialogState(false)
  const [providersToConnect, setProvidersToConnect] = useState<Provider[]>([])
  const [templates, setTemplates] = useState<Array<{ id: number; title: string }>>([])
  const [hideViewScreenedRequestsButton, setHideViewScreenedRequestsButton] = useState(false)
  const [stepOffset, setStepOffset] = useState(0)

  const [modalPurpose, setModalPurpose] = useState<ProvidersModalPurpose>(ProvidersModalPurpose.InitialInvitation)
  const [exactMatches, setExactMatches] = useState<Array<ProviderOrganizationResult>>([])

  const updateProviderToConnect = (updatedProvider: Provider) => {
    setProvidersToConnect(prev => [...prev.filter(p => p.id !== updatedProvider.id), updatedProvider])
  }

  const removeProviderFromExactMatches = useCallback((providerId: number) => {
    setExactMatches(prev => prev.filter(match => match.providerId !== providerId))
    setProvidersToConnect(prev => prev.filter(provider => provider.id !== providerId))
  }, [])

  const handleOpenConnectDialog = (
    providers: Provider[],
    modalPurpose: ProvidersModalPurpose,
    matches?: Array<ProviderOrganizationResult>,
    templates?: Array<{ id: number; title: string }>,
    hideViewScreenedRequestsButton?: boolean,
    stepOffset = 0,
  ) => {
    setProvidersToConnect(providers)
    setModalPurpose(modalPurpose)
    if (matches) {
      setExactMatches(matches)
    }
    if (templates) {
      setTemplates(templates)
    }
    setHideViewScreenedRequestsButton(Boolean(hideViewScreenedRequestsButton))
    setStepOffset(stepOffset)
    openConnectDialog()
  }

  const handleCloseConnectDialog = () => {
    closeConnectDialog()
    setProvidersToConnect([])
    setExactMatches([])
    setTemplates([])
    setHideViewScreenedRequestsButton(false)
    setStepOffset(0)
  }

  const contextValue = useMemo(
    () => ({
      isConnectDialogOpen,
      handleCloseConnectDialog,
      handleOpenConnectDialog,
      providersToConnect,
      updateProviderToConnect,
      modalPurpose,
      exactMatches,
      templates,
      hideViewScreenedRequestsButton,
      stepOffset,
      removeProviderFromExactMatches,
    }),
    [
      isConnectDialogOpen,
      closeConnectDialog,
      handleOpenConnectDialog,
      providersToConnect,
      modalPurpose,
      setProvidersToConnect,
      exactMatches,
      setExactMatches,
      templates,
      setTemplates,
      hideViewScreenedRequestsButton,
      stepOffset,
      removeProviderFromExactMatches,
    ],
  )

  return <ProvidersModalContext.Provider value={contextValue}>{children}</ProvidersModalContext.Provider>
}

export default ProvidersModalContextProvider
